import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { ParkInput } from '@models/Models';
import useParkInputs from './useParkInputs';
import { ParkInputStatus } from '@models/Enums';
import useProject from './useProject';

const useExcel = () => {
    const { getAllParkInputs, getAllParkInputsByProject } = useParkInputs();
    const { getProjectByID } = useProject();

    const exportAllInputs = async () => {
        console.time("Excel Export Time"); // Start timer

        const results = await getAllParkInputs(250, 0);

        if (!results.success) {
            console.log(results.message);
            alert(results.message);
            return;
        }

        let data: ParkInput[] = results.data;

        if (data.length === 0) {
            alert("Keine Daten zum Exportieren.");
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Ladezone-Belegung');

        // **Define Headers with Line Breaks**
        const headers = [
            "ID", "Projekt", "Ankunft bearbeitet\nvon Mitarbeiter", "Ankunft\nDatum Zeitstempel",
            "1. SMS\nDatum Zeitstempel", "Ankunft\nZeit", "2. SMS\nDatum Zeitstempel",
            "Abfahrt bearbeitet\nvon Mitarbeiter", "Abfahrt\nDatum Zeitstempel", "Abfahrt\nZeit",
            "Aufenthalts\ndauer", "Foto", "Ladezone", "KFZ KZ", "Adresse", "Haus Nr.",
            "Stiege", "TOP Nr.", "Telefon Nr.", "Unterschrift", "Status", "ABBRUCH",
            "Begründung\nbei Abbruch"
        ];

        worksheet.addRow(headers);

        // Format Header Row
        const headerRow = worksheet.getRow(1);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFDD99' } };
            cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        });
        worksheet.getRow(1).height = 30; // Adjust header height

        // **Set All Column Widths to 20**
        worksheet.columns = headers.map(() => ({ width: 20 }));

        // **Step 1: Convert All Images in Parallel**
        console.time("Image Processing Time");

        const imagePromises = data.map(async (item) => ({
            ...item,
            imageBase64: item.image ? await convertImageToBase64(item.image) : null,
            signatureBase64: item.signature ? await convertImageToBase64(item.signature) : null
        }));

        const processedData = await Promise.all(imagePromises);

        console.timeEnd("Image Processing Time"); // End image conversion timer

        // **Step 2: Process Rows**
        console.time("Excel Row Processing Time");

        processedData.forEach((item, rowIndex) => {
            const rowNumber = rowIndex + 1; // Adjust for header row

            worksheet.addRow([
                item.$id,
                item.project?.name || '',
                (item.creator.firstName + " " + item.creator.lastName) || '',
                item.firstSMSsentAt ? new Date(item.firstSMSsentAt).toLocaleString() : '',
                item.firstSMSsentAt ? new Date(item.firstSMSsentAt).toLocaleString() : '',
                item.firstSMSsentAt ? new Date(item.firstSMSsentAt).toLocaleTimeString() : '',
                item.secondSMSsendAt ? new Date(item.secondSMSsendAt).toLocaleString() : '',
                (item.creator.firstName + " " + item.creator.lastName) || '',
                item.endetAt ? new Date(item.endetAt).toLocaleString() : '',
                item.endetAt ? new Date(item.endetAt).toLocaleTimeString() : '',
                calculateDuration(item.firstSMSsentAt as string, item.endetAt as string), // Aufenthaltsdauer (optional calculation)
                "", // Foto placeholder
                item.loadingZone || '',
                item.vehiclePlate || '',
                item.address?.street || '',
                item.address?.hausnummer || '',
                item.address?.stiege || '',
                item.topNummer || '',
                item.phoneNumber || '',
                "", // Unterschrift placeholder
                item.status || '',
                item.status === ParkInputStatus.CONFLICT ? "Ja" : "Nein", // ABBRUCH (optional)
                "" // Begründung bei Abbruch
            ]);

            worksheet.getRow(rowNumber).height = 100;// Set row height

            // **Insert Images**
            if (item.imageBase64) {
                const imageId = workbook.addImage({
                    base64: item.imageBase64,
                    extension: 'png',
                });

                worksheet.addImage(imageId, { tl: { col: 11, row: rowNumber }, ext: { width: 120, height: 100 } });
                worksheet.getRow(rowNumber).commit();
            }

            if (item.signatureBase64) {
                const signatureId = workbook.addImage({
                    base64: item.signatureBase64,
                    extension: 'png',
                });

                worksheet.addImage(signatureId, { tl: { col: 19, row: rowNumber }, ext: { width: 140, height: 100 } });
                worksheet.getRow(rowNumber).commit();
            }
        });

        console.timeEnd("Excel Row Processing Time"); // End row processing timer

        worksheet.eachRow((row, rowNumber) => {
            if (rowNumber > 1) {
                row.height = 100; // Ensure all image rows have the correct height
            }
        });

        // **Step 3: Generate and Save File**
        console.time("Excel File Generation Time");

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, 'Ladezone_Belegung.xlsx');

        console.timeEnd("Excel File Generation Time"); // End file generation timer
        console.timeEnd("Excel Export Time"); // End total processing time
    };

    const exportProject = async (projectId: string) => {
        console.time("Excel Export Time"); // Start timer

        const {data: project} = await getProjectByID(projectId);
        const results = await getAllParkInputsByProject(250, 0, projectId);

        if (!results.success) {
            console.log(results.message);
            alert(results.message);
            return;
        }

        let data: ParkInput[] = results.data;

        if (data.length === 0) {
            alert("Keine Daten zum Exportieren.");
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Ladezone-Belegung');

        // **Define Headers with Line Breaks**
        const headers = [
            "ID", "Projekt", "Ankunft bearbeitet\nvon Mitarbeiter", "Ankunft\nDatum Zeitstempel",
            "1. SMS\nDatum Zeitstempel", "Ankunft\nZeit", "2. SMS\nDatum Zeitstempel",
            "Abfahrt bearbeitet\nvon Mitarbeiter", "Abfahrt\nDatum Zeitstempel", "Abfahrt\nZeit",
            "Aufenthalts\ndauer", "Foto", "Ladezone", "KFZ KZ", "Adresse", "Haus Nr.",
            "Stiege", "TOP Nr.", "Telefon Nr.", "Unterschrift", "Status", "ABBRUCH",
            "Begründung\nbei Abbruch"
        ];

        worksheet.addRow(headers);

        // Format Header Row
        const headerRow = worksheet.getRow(1);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFDD99' } };
            cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        });
        worksheet.getRow(1).height = 30; // Adjust header height

        // **Set All Column Widths to 20**
        worksheet.columns = headers.map(() => ({ width: 20 }));

        // **Step 1: Convert All Images in Parallel**
        console.time("Image Processing Time");

        const imagePromises = data.map(async (item) => ({
            ...item,
            imageBase64: item.image ? await convertImageToBase64(item.image) : null,
            signatureBase64: item.signature ? await convertImageToBase64(item.signature) : null
        }));

        const processedData = await Promise.all(imagePromises);

        console.timeEnd("Image Processing Time"); // End image conversion timer

        // **Step 2: Process Rows**
        console.time("Excel Row Processing Time");

        processedData.forEach((item, rowIndex) => {
            const rowNumber = rowIndex + 1; // Adjust for header row

            worksheet.addRow([
                item.$id,
                item.project?.name || '',
                (item.creator.firstName + " " + item.creator.lastName) || '',
                item.firstSMSsentAt ? new Date(item.firstSMSsentAt).toLocaleString() : '',
                item.firstSMSsentAt ? new Date(item.firstSMSsentAt).toLocaleString() : '',
                item.firstSMSsentAt ? new Date(item.firstSMSsentAt).toLocaleTimeString() : '',
                item.secondSMSsendAt ? new Date(item.secondSMSsendAt).toLocaleString() : '',
                (item.creator.firstName + " " + item.creator.lastName) || '',
                item.endetAt ? new Date(item.endetAt).toLocaleString() : '',
                item.endetAt ? new Date(item.endetAt).toLocaleTimeString() : '',
                calculateDuration(item.firstSMSsentAt as string, item.endetAt as string), // Aufenthaltsdauer (optional calculation)
                "", // Foto placeholder
                item.loadingZone || '',
                item.vehiclePlate || '',
                item.address?.street || '',
                item.address?.hausnummer || '',
                item.address?.stiege || '',
                item.topNummer || '',
                item.phoneNumber || '',
                "", // Unterschrift placeholder
                item.status || '',
                item.status === ParkInputStatus.CONFLICT ? "Ja" : "Nein", // ABBRUCH (optional)
                "" // Begründung bei Abbruch
            ]);

            worksheet.getRow(rowNumber).height = 100;// Set row height 

            // **Insert Images**
            if (item.imageBase64) {
                const imageId = workbook.addImage({
                    base64: item.imageBase64,
                    extension: 'png',
                });

                worksheet.addImage(imageId, { tl: { col: 11, row: rowNumber }, ext: { width: 120, height: 100 } });
                worksheet.getRow(rowNumber).commit();
            }

            if (item.signatureBase64) {
                const signatureId = workbook.addImage({
                    base64: item.signatureBase64,
                    extension: 'png',
                });

                worksheet.addImage(signatureId, { tl: { col: 19, row: rowNumber }, ext: { width: 140, height: 100 } });
                worksheet.getRow(rowNumber).commit();
            }
        });

        // **New Sheet: Projekt-Daten**
        const projectSheet = workbook.addWorksheet('Projekt-Daten');

        const projectHeaders = ["Feld", "Wert"];
        projectSheet.addRow(projectHeaders);
        projectSheet.columns = [{ width: 100 }, { width: 50 }];

        // Format Header Row
        const projectHeaderRow = projectSheet.getRow(1);
        projectHeaderRow.eachCell((cell) => {
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFDD99' } };
            cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        });

        // Project Data Rows
        const projectData = [
            ["Projekt ID", project.$id],
            ["Projektname", project.name],
            ["Maximale Zeit (min)", project.maxTime.toString()],
            ["Warnung nach (min)", project.warningAfter.toString()],
            ["Status", project.status],
            ["Infotext", project.infoText],
            ["Datenschutztext", project.datenschutzText],
            ["1. SMS Text", project.sms1Text],
            ["2. SMS Text", project.sms2Text],
            ["Erstellt am", project.createdAt ? new Date(project.createdAt).toLocaleString() : ""]
        ];

        projectData.forEach((row) => projectSheet.addRow(row));

        // **Insert Project Logos**
        if (project.projectLogo) {
            const logoId = workbook.addImage({ base64: await convertImageToBase64(project.projectLogo), extension: 'png' });
            projectSheet.addImage(logoId, { tl: { col: 2, row: 1 }, ext: { width: 80, height: 70 } });
        }

        if (project.logoClient) {
            const clientLogoId = workbook.addImage({ base64: await convertImageToBase64(project.logoClient), extension: 'png' });
            projectSheet.addImage(clientLogoId, { tl: { col: 4, row: 1 }, ext: { width: 80, height: 70 } });
        }

        console.timeEnd("Excel Row Processing Time"); // End row processing timer

        worksheet.eachRow((row, rowNumber) => {
            if (rowNumber > 1) {
                row.height = 100; // Ensure all image rows have the correct height
            }
        });

        // **Step 3: Generate and Save File**
        console.time("Excel File Generation Time");

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, 'Ladezone_Belegung.xlsx');


    };

    const convertImageToBase64 = async (imageUrl: string): Promise<string> => {
        try {
            const response = await fetch(imageUrl);
            const blob = await response.blob();
            return await new Promise((resolve) => {
                const reader = new FileReader();
                reader.readAsDataURL(blob);
                reader.onloadend = () => resolve(reader.result as string);
            });
        } catch (error) {
            console.error("Error converting image to Base64:", error);
            return ""; // Return an empty string if conversion fails
        }
    };

    const calculateDuration = (arrivalTimestamp: string | null, departureTimestamp: string | null): string => {
        if (!arrivalTimestamp || !departureTimestamp) {
            return "N/A"; // Missing timestamps
        }
    
        const arrival = new Date(arrivalTimestamp);
        const departure = new Date(departureTimestamp);
    
        if (isNaN(arrival.getTime()) || isNaN(departure.getTime())) {
            return "Invalid Date";
        }
    
        // Calculate the difference in milliseconds
        const diffMs = departure.getTime() - arrival.getTime();
    
        if (diffMs < 0) {
            return "Invalid Time Range"; // If departure is before arrival
        }
    
        // Convert to days, hours, and minutes
        const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
        // Format output
        if (days > 0) {
            return `${days} Tage ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        } else {
            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        }
    };

    return {
        exportAllInputs,
        exportProject
    };
};

export default useExcel;
